import { useEffect, useMemo, useState } from "react";

import {
  PalAutoComplete,
  PalButton,
  PalDatePicker,
  PalDialog,
  PalMenu,
  PalMenuItem,
  PalNumberFormat,
  PalTextField,
  PalTooltip,
  PalTypography,
} from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import styled from "@emotion/styled";
import { Add, Delete, Edit, MoreVert, Link, LinkOff } from "@mui/icons-material";
import { Box, FormHelperText, Grid, lighten, ListItemIcon, ListItemText, Tab, Tabs } from "@mui/material";

import useProjectChange from "~common/hooks/useProjectChange";
import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRPage from "~components/Generic/PRPage";
import PalIconButton from "~components/mui/PalIconButton";
import { billSettingsCurrencyCode, billSettingsCurrencyCodeMap } from "~constants";
import DateHelper from "~helpers/DateHelper";
import DialogHelper from "~helpers/DialogHelper";
import HistoryHelper from "~helpers/HistoryHelper";
import { createOrUpdateBill, getBill, getBillSettingList, setBill } from "~store/billingAndInvoicing/actions";
import { selectBill, selectBillSettingList } from "~store/billingAndInvoicing/selectors";
import { selectCurrentProject } from "~store/user/selectors";

const StyledTd = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[200]};
  ${(props) => props.noBorder && "border-bottom: none;"}
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${(props) =>
    props.custom &&
    `
    background-color: ${lighten(props.theme.palette.warning.main, 0.95)};
  `}
`;

const StyledTh = styled.th`
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[200]};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function BillItemAddEditModalContent({ item, currencyCode, get, billItems }) {
  const { t } = useTranslation();
  const currentProject = useSelector(selectCurrentProject);
  const isEdit = Boolean(item?.id);
  const [tabIndex, setTabIndex] = useState(isEdit && item?.billing_settings_item === 0 ? 1 : 0);
  const [isVatLocked, setIsVatLocked] = useState(true);

  const handleToggleVatLock = () => {
    setIsVatLocked((prev) => !prev);
  };

  useEffect(() => {
    if (isEdit && !item?.billing_settings_item) {
      setTabIndex(1);
    } else {
      setTabIndex(0);
    }
  }, [isEdit, item?.billing_settings_item]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      billing_settings_item: item?.billing_settings_item ?? 0,
      bill_item_details: {
        item_name: item?.bill_item_details?.item_name ?? "",
        item_unit_price: item?.bill_item_details?.item_unit_price ?? 0,
      },
      quantity: item?.quantity ?? 1,
      vat_rate: item?.vat_rate ?? 18,
      base_amount: item?.base_amount ?? 0,
      vat_amount: item?.vat_amount ?? 0,
      tax_exemption_reason_code: 1,
    },
    validationSchema: Yup.object({
      billing_settings_item: Yup.mixed().when([], {
        is: () => tabIndex === 0,
        then: Yup.number().required(t("common.required")).min(1, t("common.required")),
      }),
      bill_item_details: Yup.object().when([], {
        is: () => tabIndex === 1,
        then: Yup.object({
          item_name: Yup.string().required(t("common.required")),
          item_unit_price: Yup.number()
            .required(t("common.required"))
            .min(0.01, t("dashboard.billItemAddEditModal.minPrice")),
        }),
      }),
      quantity: Yup.number().required(t("common.required")),
      vat_rate: Yup.number().required(t("common.required")),
      base_amount: Yup.number().required(t("common.required")),
    }),
    onSubmit: (values) => {
      get(values)();
    },
  });

  const handleTabChange = (_, newIndex) => {
    setTabIndex(newIndex);
  };

  // Assuming numberFormatter is a utility function for formatting numbers
  // You might need to define it or import it if it's not globally available
  const numberFormatter = (value) => {
    if (typeof value !== "number") return value;
    return (
      <PalNumberFormat
        render
        NumericFormatProps={{
          decimalScale: 2,
          fixedDecimalScale: true,
          allowNegative: false,
          decimalSeparator: ".",
          thousandSeparator: ",",
          prefix: `${billSettingsCurrencyCodeMap[currencyCode]} `,
        }}
        value={value}
      />
    );
  };

  const billItemsOptions = useMemo(() => {
    return billItems.map((item) => ({
      label: `${item.item_name}`,
      value: item.id,
    }));
  }, [billItems]);

  const selectedBillingSettingsItem = useMemo(() => {
    return billItems.find((item) => item.id === formik.values.billing_settings_item);
  }, [billItems, formik.values.billing_settings_item]);

  const handleChangeSelect = (e, value) => {
    formik.setFieldValue("billing_settings_item", value);
    const selectedItem = billItems.find((item) => item.id === value);
    // if (selectedItem) {
    //   formik.setFieldValue("bill_item_details.item_name", selectedItem.item_name);
    //   formik.setFieldValue("bill_item_details.item_unit_price", selectedItem.item_unit_price);
    // }

    const unitPrice = selectedItem?.item_unit_price;
    formik.setFieldValue("base_amount", formik.values.quantity * unitPrice);
    formik.setFieldValue("vat_amount", (formik.values.quantity * unitPrice * formik.values.vat_rate) / 100);
  };

  const handleChangeQuantity = (e) => {
    const value = e.target.value;
    formik.setFieldValue("quantity", value);
    const unitPrice =
      tabIndex === 0 ? selectedBillingSettingsItem?.item_unit_price : formik.values.bill_item_details.item_unit_price;
    formik.setFieldValue("base_amount", value * unitPrice);
    formik.setFieldValue("vat_amount", (value * unitPrice * formik.values.vat_rate) / 100);
  };

  const handleChangeVatRate = (e) => {
    const value = e.target.value;
    const unitPrice =
      tabIndex === 0 ? selectedBillingSettingsItem?.item_unit_price : formik.values.bill_item_details.item_unit_price;
    formik.setFieldValue("vat_rate", value);
    formik.setFieldValue("vat_amount", (formik.values.quantity * unitPrice * value) / 100);
  };

  const totalPriceWithVat = useMemo(() => {
    return Number(formik.values.base_amount) + Number(formik.values.vat_amount);
  }, [formik.values.base_amount, formik.values.vat_amount]);

  useEffect(() => {
    if (isVatLocked) {
      const unitPrice =
        tabIndex === 0 ? selectedBillingSettingsItem?.item_unit_price : formik.values.bill_item_details.item_unit_price;
      formik.setFieldValue("vat_amount", (formik.values.quantity * unitPrice * formik.values.vat_rate) / 100);
    }
  }, [
    formik.values.quantity,
    formik.values.vat_rate,
    isVatLocked,
    selectedBillingSettingsItem?.item_unit_price,
    tabIndex,
  ]);
  const handleChangeCustomItemUnitPrice = (e) => {
    const value = e.target.value;
    formik.setFieldValue("bill_item_details.item_unit_price", value);
    formik.setFieldValue("base_amount", formik.values.quantity * value);
    formik.setFieldValue("vat_amount", (formik.values.quantity * value * formik.values.vat_rate) / 100);
  };
  const isExistingItemInputsDisabled = useMemo(() => {
    return tabIndex === 0 && !formik.values.billing_settings_item;
  }, [formik.values.billing_settings_item, tabIndex]);
  return (
    <PalDialog fullWidth open maxWidth="sm" scroll="body" onClose={get(false)}>
      <PalDialog.Title>
        {isEdit ? t("component.billItemAddEditModal.edit") : t("component.billItemAddEditModal.create")}
      </PalDialog.Title>
      <PalDialog.Content dividers={false}>
        <Tabs value={tabIndex} variant="fullWidth" onChange={handleTabChange}>
          <Tab label={t("component.billItemAddEditModal.existingItems")} />
          <Tab label={t("component.billItemAddEditModal.customItems")} />
        </Tabs>

        <Box mt={2}>
          {tabIndex === 0 && (
            <>
              <PalAutoComplete
                fullWidth
                isPrimitiveValue
                select
                invalid={formik.touched.billing_settings_item && formik.errors.billing_settings_item}
                label={t("component.billItemAddEditModal.existingSelect")}
                name="billing_settings_item"
                options={billItemsOptions}
                value={formik.values.billing_settings_item}
                onChange={handleChangeSelect}
              />
              {selectedBillingSettingsItem && (
                <Box mt={2}>
                  <PalTypography>
                    {t("component.billItemAddEditModal.itemName")}: {selectedBillingSettingsItem.item_name}
                    <br />
                    {t("component.billItemAddEditModal.itemUnitPrice")}:{" "}
                    {numberFormatter(selectedBillingSettingsItem.item_unit_price)}
                  </PalTypography>
                </Box>
              )}
            </>
          )}

          {tabIndex === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PalTextField
                  fullWidth
                  invalid={formik.touched.bill_item_details?.item_name && formik.errors.bill_item_details?.item_name}
                  label={t("component.billItemAddEditModal.itemName")}
                  name="bill_item_details.item_name"
                  value={formik.values.bill_item_details?.item_name}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <PalNumberFormat
                  fullWidth
                  invalid={
                    formik.touched.bill_item_details?.item_unit_price &&
                    formik.errors.bill_item_details?.item_unit_price
                  }
                  label={t("component.billItemAddEditModal.itemUnitPrice")}
                  name="bill_item_details.item_unit_price"
                  NumericFormatProps={{
                    decimalScale: 2,
                    fixedDecimalScale: true,
                    allowNegative: false,
                    decimalSeparator: ".",
                    thousandSeparator: ",",
                    prefix: `${billSettingsCurrencyCodeMap[currencyCode]} `,
                  }}
                  value={formik.values.bill_item_details?.item_unit_price}
                  onChange={handleChangeCustomItemUnitPrice}
                />
              </Grid>
            </Grid>
          )}

          {/* Common Fields */}
          <Grid container columns={17} mt={1} spacing={2}>
            <Grid item xs={8}>
              <PalNumberFormat
                fullWidth
                disabled={isExistingItemInputsDisabled}
                invalid={formik.touched.quantity && formik.errors.quantity}
                label={t("component.billItemAddEditModal.quantity")}
                name="quantity"
                NumericFormatProps={{ decimalScale: 0, fixedDecimalScale: true, allowNegative: false }}
                value={formik.values.quantity}
                onBlur={formik.handleBlur}
                onChange={handleChangeQuantity}
              />
            </Grid>
            <Grid item xs={1}></Grid>
            <Grid item xs={8}>
              <PalNumberFormat
                disabled
                fullWidth
                invalid={formik.touched.base_amount && formik.errors.base_amount}
                label={t("component.billItemAddEditModal.baseAmount")}
                name="base_amount"
                NumericFormatProps={{ decimalScale: 0, fixedDecimalScale: true, allowNegative: false }}
                value={formik.values.base_amount}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
          <Grid container columns={17} mb={1} mt={2} spacing={2}>
            <Grid item xs={8}>
              <PalNumberFormat
                fullWidth
                disabled={isExistingItemInputsDisabled}
                invalid={formik.touched.vat_rate && formik.errors.vat_rate}
                label={t("component.billItemAddEditModal.vatRate")}
                name="vat_rate"
                NumericFormatProps={{ decimalScale: 0, fixedDecimalScale: true, allowNegative: false, suffix: " %" }}
                value={formik.values.vat_rate}
                onBlur={formik.handleBlur}
                onChange={handleChangeVatRate}
              />
            </Grid>
            <Grid
              item
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
              xs={1}
            >
              <PalIconButton size="small" sx={{ mt: 1 }} onClick={handleToggleVatLock}>
                {isVatLocked ? <Link /> : <LinkOff />}
              </PalIconButton>
            </Grid>

            <Grid item xs={8}>
              <PalNumberFormat
                fullWidth
                disabled={isVatLocked || isExistingItemInputsDisabled}
                invalid={formik.touched.vat_amount && formik.errors.vat_amount}
                label={t("component.billItemAddEditModal.vatAmount")}
                name="vat_amount"
                NumericFormatProps={{ decimalScale: 2, fixedDecimalScale: true, allowNegative: false }}
                value={formik.values.vat_amount}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
          <PalTypography>
            {t("component.billItemAddEditModal.totalPriceWithVat")}:{" "}
            <PalNumberFormat
              render
              NumericFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: false,
                decimalSeparator: ".",
                thousandSeparator: ",",
                prefix: `${billSettingsCurrencyCodeMap[currencyCode]} `,
              }}
              value={totalPriceWithVat}
            />
          </PalTypography>
        </Box>
      </PalDialog.Content>

      <PalDialog.Actions>
        <PalButton color="primary" variant="contained" onClick={() => formik.handleSubmit()}>
          {isEdit ? t("common.update") : t("common.submit")}
        </PalButton>
        <PalButton onClick={() => get(false)()}>{t("common.cancel")}</PalButton>
      </PalDialog.Actions>
    </PalDialog>
  );
}

const BillItemAddEditModal = withCardon(BillItemAddEditModalContent, { destroyOnHide: true });

const BillSettingItem = ({ item, billSettingsItem, onEdit, onDelete, currencyCode }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickEdit = () => {
    handleClose();
    onEdit(item);
  };
  const handleClickDelete = () => {
    handleClose();
    onDelete(item);
  };

  const isCustomItem = useMemo(() => {
    return !item?.billing_settings_item;
  }, [item]);

  const itemTotalPriceWithVat = useMemo(() => {
    return Number(item?.base_amount || 0) + Number(item?.vat_amount || 0);
  }, [item?.base_amount, item?.vat_amount]);

  return (
    <tr>
      <StyledTd custom={isCustomItem}>
        <PalTooltip title={isCustomItem ? item.bill_item_details?.item_name : billSettingsItem?.item_name}>
          <span>{isCustomItem ? item.bill_item_details?.item_name : billSettingsItem?.item_name}</span>
        </PalTooltip>
      </StyledTd>
      <StyledTd custom={isCustomItem}>
        <PalTooltip title={isCustomItem ? item.bill_item_details?.item_unit_price : billSettingsItem?.item_unit_price}>
          <span>
            <PalNumberFormat
              render
              NumericFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: false,
                decimalSeparator: ".",
                thousandSeparator: ",",
                prefix: `${billSettingsCurrencyCodeMap[currencyCode] || billSettingsCurrencyCode.USD} `,
              }}
              value={isCustomItem ? item.bill_item_details?.item_unit_price : billSettingsItem?.item_unit_price}
            />
          </span>
        </PalTooltip>
      </StyledTd>

      <StyledTd custom={isCustomItem}>
        <PalTooltip title={item?.quantity}>
          <span>{item?.quantity}</span>
        </PalTooltip>
      </StyledTd>

      <StyledTd custom={isCustomItem}>
        <PalTooltip title={item?.base_amount}>
          <span>
            <PalNumberFormat
              render
              NumericFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: false,
                decimalSeparator: ".",
                thousandSeparator: ",",
                prefix: `${billSettingsCurrencyCodeMap[currencyCode] || billSettingsCurrencyCode.USD} `,
              }}
              value={item?.base_amount}
            />
          </span>
        </PalTooltip>
      </StyledTd>

      <StyledTd custom={isCustomItem}>
        <PalTooltip title={item?.vat_rate}>
          <span>{item?.vat_rate} %</span>
        </PalTooltip>
      </StyledTd>

      <StyledTd custom={isCustomItem}>
        <PalTooltip title={item?.vat_amount}>
          <span>
            <PalNumberFormat
              render
              NumericFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: false,
                decimalSeparator: ".",
                thousandSeparator: ",",
                prefix: `${billSettingsCurrencyCodeMap[currencyCode] || billSettingsCurrencyCode.USD} `,
              }}
              value={item?.vat_amount}
            />
          </span>
        </PalTooltip>
      </StyledTd>

      <StyledTd custom={isCustomItem} style={{ fontWeight: "bold" }}>
        <PalNumberFormat
          render
          NumericFormatProps={{
            decimalScale: 2,
            fixedDecimalScale: true,
            allowNegative: false,
            decimalSeparator: ".",
            thousandSeparator: ",",
            prefix: `${billSettingsCurrencyCodeMap[currencyCode] || billSettingsCurrencyCode.USD} `,
          }}
          value={itemTotalPriceWithVat}
        />
      </StyledTd>

      <StyledTd custom={isCustomItem}>
        <Box display="flex" justifyContent="flex-end">
          <PalTooltip title={t("common.actions")}>
            <span>
              <PalIconButton onClick={handleMenuOpen}>
                <MoreVert />
              </PalIconButton>
            </span>
          </PalTooltip>
          <PalMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <PalMenuItem onClick={handleClickEdit}>
              <ListItemIcon>
                <Edit />
              </ListItemIcon>
              <ListItemText>{t("common.edit")}</ListItemText>
            </PalMenuItem>
            <PalMenuItem onClick={handleClickDelete}>
              <ListItemIcon
                sx={{
                  color: (theme) => theme.palette.error.main,
                }}
              >
                <Delete />
              </ListItemIcon>
              <ListItemText>{t("common.delete")}</ListItemText>
            </PalMenuItem>
          </PalMenu>
        </Box>
      </StyledTd>
    </tr>
  );
};

const DraftChargesEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const bill = useSelector(selectBill);

  const [stepState, setStepState] = useState({
    page1: true,
    page2: false,
  });

  const dispatch = useDispatch();

  const currentProject = useSelector(selectCurrentProject);

  const billSettingList = useSelector(selectBillSettingList);

  useEffect(() => {
    dispatch(getBillSettingList(currentProject.id));
    if (!id) return;
    dispatch(getBill(currentProject.id, id));

    return () => {
      dispatch(setBill(null));
    };
  }, [dispatch, id, currentProject.id]);

  const [initialValues, setInitialValues] = useState({
    id: null,
    billing_settings: null,
    due_date: DateHelper.getDateTimeLocal(),
    billing_period_start: DateHelper.getDateTimeLocal(),
    billing_period_end: DateHelper.getDateTimeLocal(),
    items: [],
    total_base_amount: 0,
    total_vat_amount: 0,
    notes: [],
  });

  useEffect(() => {
    setInitialValues({
      id: bill?.id || null,
      billing_settings: bill?.billing_settings || null,
      due_date: bill?.due_date ? DateHelper.getDateTimeLocal(bill?.due_date) : DateHelper.getDateTimeLocal(),
      billing_period_start: bill?.billing_period_start
        ? DateHelper.getDateTimeLocal(bill?.billing_period_start)
        : DateHelper.getDateTimeLocal(),
      billing_period_end: bill?.billing_period_end
        ? DateHelper.getDateTimeLocal(bill?.billing_period_end)
        : DateHelper.getDateTimeLocal(),
      items: bill?.items || [],
      total_base_amount: bill?.total_base_amount || 0,
      total_vat_amount: bill?.total_vat_amount || 0,
      notes: bill?.notes || [],
    });
  }, [bill]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      billing_settings: Yup.number().required(t("common.required")).nullable(),
      due_date: Yup.mixed().required(t("common.required")),
      billing_period_start: Yup.mixed().required(t("common.required")),
      billing_period_end: Yup.mixed().required(t("common.required")),
      items: Yup.array().min(1, t("dashboard.draftChargesEdit.itemsRequired")),
    }),
    onSubmit: async (values) => {
      let payload = { ...values };
      payload.items = values.items.map((item) => {
        if (!item?.billing_settings_item) {
          return {
            ...item,
            billing_settings_item: null,
          };
        } else {
          return {
            ...item,
            bill_item_details: null,
          };
        }
      });

      const totalBaseAmount = payload.items.reduce((acc, item) => acc + (item.base_amount || 0), 0);
      const totalVatAmount = payload.items.reduce((acc, item) => acc + (item.vat_amount || 0), 0);

      payload.total_base_amount = totalBaseAmount;
      payload.total_vat_amount = totalVatAmount;
      await dispatch(createOrUpdateBill(currentProject.id, payload));
      handleClickCancel();
    },
  });

  const selectedBillSettingItem = useMemo(() => {
    return billSettingList.find((item) => item.id === formik.values.billing_settings);
  }, [billSettingList, formik.values.billing_settings]);

  useProjectChange(() => {
    HistoryHelper.push("/billing-and-invoicing/draft-charges", {
      scope: "dashboard",
    });
  }, []);

  window.f = formik;
  const handleClickAddNewItem = async () => {
    const result = await BillItemAddEditModal.show({
      item: null,
      billItems: selectedBillSettingItem.items,
      currencyCode: selectedBillSettingItem.currency_code,
    });
    if (!result) return;
    formik.setFieldValue("items", [...formik.values.items, { ...result }]);
  };

  const parentName = useMemo(
    () => [
      {
        label: t("layout.sidebar.draftCharges"),
        url: "/billing-and-invoicing/draft-charges",
      },
      {
        label: id ? t("dashboard.draftChargesEdit.edit") : t("dashboard.draftChargesEdit.create"),
      },
    ],
    [id, t]
  );
  const handleClickCancel = () => {
    HistoryHelper.goBack("/billing-and-invoicing/draft-charges/", { scope: "dashboard" });
  };

  const handleChangeBillSettingsCurrencyCode = (e, value) => {
    // setBillSettingsCurrencyCode(value);
    formik.setFieldValue("billing_settings", value);
  };

  const billSettingListOptions = useMemo(() => {
    return billSettingList.map((item) => ({
      label: `${item.currency_code} (${item.items?.length || 0})`,
      value: item.id,
    }));
  }, [billSettingList]);
  const handleBillingPeriodStartChange = (value) => {
    formik.setFieldValue("billing_period_start", DateHelper.getDateTimeLocal(value));
    if (value.isAfter(formik.values.billing_period_end)) {
      formik.setFieldValue("billing_period_end", DateHelper.getDateTimeLocal(value));
    }
  };

  const handleBillingPeriodEndChange = (value) => {
    formik.setFieldValue("billing_period_end", DateHelper.getDateTimeLocal(value));
  };

  const handleChangeNotes = (e) => {
    const value = e.target.value;
    formik.setFieldValue("notes", [value]);
  };

  const handleChangeDueDate = (value) => {
    formik.setFieldValue("due_date", DateHelper.getDateTimeLocal(value));
  };
  console.log("formik.values", formik.values);
  return (
    <PRContainer
      description={t("dashboard.chatLabelsEdit.description")}
      name={t("layout.sidebar.billingAndInvoicing")}
      parentName={parentName}
    >
      <PRPage>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PalTypography
              color="text.secondary"
              sx={{
                my: 1,
              }}
              variant="body2"
            >
              {t("dashboard.draftChargesEdit.billSettingsCurrencyCodeDesc")}
            </PalTypography>
            <Box
              mb={4}
              pt={1}
              width={{
                xs: 1,
                sm: 1,
                md: 1 / 2,
              }}
            >
              <PalAutoComplete
                isPrimitiveValue
                invalid={formik.touched.billing_settings && formik.errors.billing_settings}
                label={t("dashboard.draftChargesEdit.currencyCodeType")}
                options={billSettingListOptions}
                value={formik.values.billing_settings}
                onChange={handleChangeBillSettingsCurrencyCode}
              />
            </Box>
          </Grid>
          {!!formik.values.billing_settings && (
            <>
              <Grid item md={4} xs={12}>
                <PalDatePicker
                  fullWidth
                  invalid={formik.touched.due_date && formik.errors.due_date}
                  label={t("dashboard.draftChargesEdit.dueDate")}
                  name="due_date"
                  value={formik.values.due_date}
                  onBlur={formik.handleBlur}
                  onChange={handleChangeDueDate}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <PalDatePicker
                  fullWidth
                  invalid={formik.touched.billing_period_start && formik.errors.billing_period_start}
                  label={t("dashboard.draftChargesEdit.billingPeriodStart")}
                  name="billing_period_start"
                  value={formik.values.billing_period_start}
                  onBlur={formik.handleBlur}
                  onChange={handleBillingPeriodStartChange}
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <PalDatePicker
                  fullWidth
                  disabled={!formik.values.billing_period_start} // Start date seçilene kadar devre dışı
                  invalid={formik.touched.billing_period_end && formik.errors.billing_period_end}
                  label={t("dashboard.draftChargesEdit.billingPeriodEnd")}
                  minDate={formik.values.billing_period_start} // Start date'den önce seçilemez
                  name="billing_period_end"
                  value={formik.values.billing_period_end}
                  onBlur={formik.handleBlur}
                  onChange={handleBillingPeriodEndChange}
                />
              </Grid>
            </>
          )}
        </Grid>
        {!!formik.values.billing_settings && (
          <Grid container pt={2} spacing={2}>
            <Grid item xs={12}>
              <Box display={"flex"} justifyContent={"flex-end"}>
                <PalButton
                  disabled={!formik.values.billing_settings}
                  startIcon={<Add />}
                  onClick={handleClickAddNewItem}
                >
                  {t("common.add")}
                </PalButton>
              </Box>

              <Box mt={1} py={2}>
                {!!formik.values?.items?.length && (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <Box component="tr" sx={{ background: (theme) => theme.palette.grey[100] }}>
                        <StyledTh>{t("component.billSettingsItem.itemName")}</StyledTh>
                        <StyledTh>{t("component.billSettingsItem.itemUnitPrice")}</StyledTh>
                        <StyledTh>{t("component.billSettingsItem.itemQuantity")}</StyledTh>
                        <StyledTh>{t("component.billSettingsItem.itemBaseAmount")}</StyledTh>
                        <StyledTh>{t("component.billSettingsItem.vatRate")}</StyledTh>
                        <StyledTh>{t("component.billSettingsItem.vatAmount")}</StyledTh>
                        <StyledTh>{t("component.billSettingsItem.totalPrice")}</StyledTh>
                        <StyledTh></StyledTh>
                      </Box>
                    </thead>
                    <tbody>
                      {formik.values.items.map((item, index) => {
                        const relatedBillSettingSubItem = selectedBillSettingItem.items.find(
                          (i) => i.id === item.billing_settings_item
                        );

                        return (
                          <BillSettingItem
                            key={index}
                            billSettingsItem={relatedBillSettingSubItem}
                            currencyCode={selectedBillSettingItem?.currency_code}
                            item={item}
                            onDelete={async (item) => {
                              if (
                                !(await DialogHelper.showQuestionDeleteWithFormat(
                                  relatedBillSettingSubItem?.item_name || item?.bill_item_details?.item_name
                                ))
                              ) {
                                return;
                              }

                              formik.setFieldValue(
                                "items",
                                formik.values.items.filter((_, i) => i !== index)
                              );
                            }}
                            onEdit={async (item) => {
                              const result = await BillItemAddEditModal.show({
                                item,
                                billItems: selectedBillSettingItem.items,
                                currencyCode: selectedBillSettingItem.currency_code,
                              });
                              if (!result) return;
                              formik.setFieldValue("items", [
                                ...formik.values.items.slice(0, index),
                                { ...result },
                                ...formik.values.items.slice(index + 1),
                              ]);
                            }}
                          />
                        );
                      })}
                      <tr>
                        <StyledTd noBorder colSpan={6} style={{ textAlign: "right" }}>
                          {t("component.billSettingsItem.itemBaseAmount")}:
                        </StyledTd>
                        <StyledTd noBorder>
                          <PalNumberFormat
                            render
                            NumericFormatProps={{
                              decimalScale: 2,
                              fixedDecimalScale: true,
                              allowNegative: false,
                              decimalSeparator: ".",
                              thousandSeparator: ",",
                              prefix: `${billSettingsCurrencyCodeMap[selectedBillSettingItem?.currency_code]} `,
                            }}
                            value={formik.values.items.reduce((acc, item) => acc + Number(item?.base_amount || 0), 0)}
                          />
                        </StyledTd>
                        <StyledTd noBorder></StyledTd> {/* Actions column for total row */}
                      </tr>
                      <tr>
                        <StyledTd noBorder colSpan={6} style={{ textAlign: "right" }}>
                          {t("component.billSettingsItem.vatAmount")}:
                        </StyledTd>
                        <StyledTd noBorder>
                          <PalNumberFormat
                            render
                            NumericFormatProps={{
                              decimalScale: 2,
                              fixedDecimalScale: true,
                              allowNegative: false,
                              decimalSeparator: ".",
                              thousandSeparator: ",",
                              prefix: `${billSettingsCurrencyCodeMap[selectedBillSettingItem?.currency_code]} `,
                            }}
                            value={formik.values.items.reduce((acc, item) => acc + Number(item?.vat_amount || 0), 0)}
                          />
                        </StyledTd>
                        <StyledTd noBorder></StyledTd> {/* Actions column for total row */}
                      </tr>
                      <tr>
                        <StyledTd noBorder colSpan={6} style={{ textAlign: "right", fontWeight: "bold" }}>
                          {t("component.billSettingsItem.totalPrice")}:
                        </StyledTd>
                        <StyledTd noBorder style={{ fontWeight: "bold" }}>
                          <PalNumberFormat
                            render
                            NumericFormatProps={{
                              decimalScale: 2,
                              fixedDecimalScale: true,
                              allowNegative: false,
                              decimalSeparator: ".",
                              thousandSeparator: ",",
                              prefix: `${billSettingsCurrencyCodeMap[selectedBillSettingItem?.currency_code]} `,
                            }}
                            value={formik.values.items.reduce(
                              (acc, item) => acc + (Number(item?.base_amount || 0) + Number(item?.vat_amount || 0)),
                              0
                            )}
                          />
                        </StyledTd>
                        <StyledTd noBorder></StyledTd> {/* Actions column for total row */}
                      </tr>
                    </tbody>
                  </table>
                )}
                {!formik.values?.items?.length && (
                  <>
                    <Box
                      sx={{
                        minHeight: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PalTypography color="text.secondary">{t("common.noItem")}</PalTypography>
                    </Box>
                    {formik.touched.items && formik.errors.items && (
                      <FormHelperText error={true}>{formik.errors.items}</FormHelperText>
                    )}
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <PalTextField
                fullWidth
                multiline
                label={t("dashboard.draftChargesEdit.notes")}
                maxRows={6}
                minRows={3}
                name="notes"
                sx={{
                  //TODO: remove this.
                  backgroundColor: "rgb(252, 252, 252)",
                }}
                value={formik.values.notes?.length > 0 ? formik.values.notes.join(", ") : ""}
                onBlur={formik.handleBlur}
                onChange={handleChangeNotes}
              />
            </Grid>
          </Grid>
        )}
        <Grid container mt={2}>
          <Grid item ml="auto">
            <PRButton outline className="me-2" onClick={handleClickCancel}>
              {t("common.cancel")}
            </PRButton>
            <PRButton onClick={formik.handleSubmit}>{id ? t("common.update") : t("common.create")}</PRButton>
          </Grid>
        </Grid>
      </PRPage>
    </PRContainer>
  );
};

export default DraftChargesEdit;
